import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AlertTriangle, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AlertsBanner() {
  const { language } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await axios.get(`${API}/alerts`);
        if (response.data && response.data.length > 0) {
          setAlerts(response.data);
        }
      } catch (error) {
        console.error('Error fetching alerts:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAlerts();
  }, []);

  // Auto-rotate alerts every 10 seconds
  useEffect(() => {
    if (alerts.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % alerts.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [alerts.length]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + alerts.length) % alerts.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % alerts.length);
  };

  // No alerts message
  if (!isLoading && alerts.length === 0) {
    return (
      <div className="bg-green-50 border-y border-green-200">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center gap-2 justify-center">
            <span className="text-xs text-green-700">
              ✓ {language === 'es' ? 'Sin avisos activos en este momento' : 'No active alerts at this time'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) return null;

  const currentAlert = alerts[currentIndex];
  
  // Color based on alert type
  const getAlertStyle = (type) => {
    if (type === 'Alerta') return 'bg-amber-50 border-amber-200 text-amber-800';
    if (type === 'Prealerta') return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    return 'bg-slate-50 border-slate-200 text-slate-600';
  };

  return (
    <div className={`border-y ${getAlertStyle(currentAlert.alertType)}`}>
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Icon and label */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="text-[10px] uppercase tracking-wide font-semibold hidden sm:inline">
              {language === 'es' ? 'Avisos' : 'Alerts'}
            </span>
          </div>
          
          {/* Alert badge */}
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0
            ${currentAlert.alertType === 'Alerta' ? 'bg-amber-200 text-amber-900' : ''}
            ${currentAlert.alertType === 'Prealerta' ? 'bg-yellow-200 text-yellow-900' : ''}
            ${currentAlert.alertType === 'Finalizada' ? 'bg-green-200 text-green-900' : ''}
          `}>
            {currentAlert.alertType}
          </span>

          {/* Zone */}
          <span className="text-[10px] font-medium flex-shrink-0 hidden sm:inline">
            {currentAlert.zone}
          </span>

          {/* Date */}
          <span className="text-[10px] opacity-70 flex-shrink-0">
            {currentAlert.date}
          </span>

          {/* Alert content - link to full details */}
          <a
            href={currentAlert.link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 min-w-0 group"
          >
            <p className="text-xs truncate group-hover:underline">
              {currentAlert.reason}
            </p>
          </a>

          {/* Navigation */}
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {alerts.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.preventDefault(); goToPrev(); }}
                  className="p-0.5 opacity-50 hover:opacity-100 transition-opacity"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="text-[10px] opacity-50 min-w-[20px] text-center">
                  {currentIndex + 1}/{alerts.length}
                </span>
                <button
                  onClick={(e) => { e.preventDefault(); goToNext(); }}
                  className="p-0.5 opacity-50 hover:opacity-100 transition-opacity"
                  aria-label="Next"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            <a
              href={currentAlert.link}
              target="_blank"
              rel="noopener noreferrer"
              className="p-0.5 opacity-50 hover:opacity-100 transition-opacity"
              aria-label="Open alert"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
